/**
 * Профиль артикула: загрузка данных.
 *  - `useProfileQuery` — один запрос к слою MCP «Бизнес» (`/business-web/query`) с отменой предыдущего;
 *  - `useProfileHeader` — шапка: фото и макет из аналитики продаж, название из каталога товаров;
 *  - `useProfileEvents` — события ПИМ артикула (цена, акции, фото, текст, поставки) для отметок на графиках.
 * Шапка и события — необязательные: нет права или ошибка — профиль остаётся без фото и отметок.
 */

import { useCallback, useEffect, useState } from 'react'
import { getSalesBreakdown, getSalesSeries } from '../analytics/sales/api'
import { defaultFilters } from '../analytics/shared/filterState'
import { addDays, todayIso } from '../analytics/shared/format'
import type { AnalyticsFilters } from '../analytics/shared/types'
import { useRemote } from '../analytics/shared/useRemote'
import { isAbortError, runExplorerQuery, toExplorerError } from '../metrics-explorer/api'
import type { ExplorerResult } from '../metrics-explorer/types'
import { productsV2API } from '../products-v2/api'
import type { GroupState, MarketplaceChoice, ProfileQueryBody } from './profileModel'
import type { ArticleProfileContext } from './types'

/** Пауза перед запросом: профиль шлёт несколько запросов подряд, а лимит nginx на /api — 10 запросов в секунду на офис. */
export const STAGGER_MS = 150

export interface ProfileQueryState extends GroupState {
  reload: () => void
}

/**
 * Запрос к слою MCP. `null` — не запрашивать. При смене тела прежний запрос отменяется, а данные прежнего тела
 * под новыми подписями не показываются (ответ привязан к ключу тела).
 */
export function useProfileQuery(body: ProfileQueryBody | null, delayMs = 0): ProfileQueryState {
  const key = body ? JSON.stringify(body) : null
  const [version, setVersion] = useState(0)
  const [state, setState] = useState<{ key: string | null; data: ExplorerResult | null; error: unknown | null; loading: boolean }>({
    key: null,
    data: null,
    error: null,
    loading: false,
  })

  useEffect(() => {
    if (!body || key === null) {
      setState({ key: null, data: null, error: null, loading: false })
      return
    }
    const controller = new AbortController()
    setState((current) => ({ key, data: current.key === key ? current.data : null, error: null, loading: true }))
    const timer = setTimeout(() => {
      runExplorerQuery(body, controller.signal)
        .then((data) => setState({ key, data, error: null, loading: false }))
        .catch((error) => {
          if (isAbortError(error)) return
          setState({ key, data: null, error: toExplorerError(error), loading: false })
        })
    }, delayMs)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
    // Тело задаётся ключом: новый объект с теми же полями запрос не повторяет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, delayMs, version])

  const reload = useCallback(() => setVersion((value) => value + 1), [])
  const current = state.key === key
  return { data: current ? state.data : null, error: current ? state.error : null, loading: key !== null && (!current || state.loading), reload }
}

/** Фильтры аналитики продаж для одного артикула за год по вчера: источник фото, макета и событий. */
export function articleFilters(article: string, marketplace: MarketplaceChoice, context: ArticleProfileContext | undefined, period: { from: string; to: string }): AnalyticsFilters {
  const sellerId = context?.sellerId !== undefined && marketplace !== 'all' && context.marketplace === marketplace ? String(context.sellerId) : null
  return {
    ...defaultFilters(),
    from: period.from,
    to: period.to,
    compare: 'none',
    marketplace: marketplace === 'all' ? null : marketplace,
    sellerIds: sellerId ? [sellerId] : [],
    articles: [article],
  }
}

export interface ProfileHeader {
  title: string | null
  imageUrl: string | null
  designCode: string | null
  /** Категория по каталогу товаров (запасная: основное описание приходит из MCP). */
  category: string | null
}

export function useProfileHeader(article: string | null, marketplace: MarketplaceChoice, context?: ArticleProfileContext): { header: ProfileHeader; loading: boolean } {
  const to = addDays(todayIso(), -1)
  const filters = article ? articleFilters(article, marketplace, context, { from: addDays(to, -364), to }) : null
  const sales = useRemote(
    JSON.stringify(['profile-sales-header', filters]),
    () => getSalesBreakdown(filters as AnalyticsFilters, { groupBy: 'article', sort: 'ordersQty', dir: 'desc', page: 1, limit: 1 }),
    'Не удалось загрузить фото артикула',
    Boolean(filters),
  )
  const product = useRemote(
    JSON.stringify(['profile-product-header', article]),
    async () => {
      const page = await productsV2API.getProducts({ limit: 1, offset: 0, articles: article as string })
      const row = page.products.find((item) => String(item?.article) === article) ?? null
      return row
        ? { title: typeof row.title === 'string' && row.title.trim() ? row.title.trim() : null, category: typeof row.category?.description === 'string' ? row.category.description : null }
        : { title: null, category: null }
    },
    'Не удалось загрузить название артикула',
    Boolean(article),
  )
  const item = sales.data?.items.find((candidate) => candidate.key === article) ?? sales.data?.items[0] ?? null
  return {
    header: {
      title: product.data?.title ?? null,
      imageUrl: item?.imageUrl ?? null,
      designCode: item?.designCode ?? null,
      category: product.data?.category ?? null,
    },
    loading: sales.loading || product.loading,
  }
}

export function useProfileEvents(article: string | null, marketplace: MarketplaceChoice, context: ArticleProfileContext | undefined, period: { from: string; to: string }) {
  const filters = article ? articleFilters(article, marketplace, context, period) : null
  const remote = useRemote(
    JSON.stringify(['profile-events', filters]),
    () => getSalesSeries(filters as AnalyticsFilters, { granularity: 'day', metric: 'ordersQty' }),
    'Не удалось загрузить события',
    Boolean(filters),
  )
  return remote.data?.events ?? []
}
